import React, { useCallback, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { type Edges } from 'react-native-safe-area-context';

import {
  BodyText,
  Form,
  TEST_GRECAPTCHA,
  TextLink,
  theme,
  useNoticeBannersStackContext,
  useReCaptcha,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { checkIfRecaptchaEnabled, formatBirthdayForSubmit, getMonthNames } from '@order/utils';

import { PageWrapper } from '../../../../components';
import { useJoinForm } from '../../hooks';
import { TermsAndConditions } from '../TermsAndConditions';

import type { JoinFormData } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const JoinForm = (props: JoinFormProps) => {
  const {
    predefinedEmail,
    isBirthdayRequired = false,
    hasPassword = false,
    shouldUseRecaptcha = false,
    isLoading,
    onPressJoin,
    onPressSignIn,
    onPressUseDifferentAccount,
  } = props;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const isZipCodeFieldEnabled = useFeatureFlag('CELS-3186-enable-zip-code-field', {
    listenForChanges: true,
  });

  // ─── ReCaptcha ───────────────────────────────────────────────────

  const isRecaptchaEnabled = checkIfRecaptchaEnabled() && shouldUseRecaptcha;
  const { reCaptchaMachine, reCaptchaRef } = useReCaptcha({
    isEnabled: isRecaptchaEnabled,
  });

  const [reCaptchaState] = reCaptchaMachine;
  const isLoadingRecaptcha = reCaptchaState.matches('loading');
  const isExecutingRecaptcha = reCaptchaState.matches('executing');

  // ────────────────────────────────────────────────────────────────────

  const { form } = useJoinForm({
    email: predefinedEmail,
    isBirthdayRequired,
    hasPassword,
    hasZipCode: isZipCodeFieldEnabled,
  });
  const { control, formState, handleSubmit } = form;
  const { errors, isValid, isSubmitting } = formState;

  // ─── Helpers ────────────────────────────────────────────────────────

  const signUpUser = useCallback(
    async (signUpFormData: JoinFormData) => {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        terms,
        birthday = '',
        grecaptcha = TEST_GRECAPTCHA,
        ...formData
      } = signUpFormData;

      const formattedBirthday = formatBirthdayForSubmit(birthday);

      await onPressJoin({
        ...formData,
        ...(formattedBirthday ? { birthday: formattedBirthday } : {}),
        terms: true,
        grecaptcha,
      });

      // reset recaptcha after request
      reCaptchaRef.current?.resetRecaptcha();
    },
    [onPressJoin, reCaptchaRef],
  );

  const onRecaptchaVerify = useCallback(
    async (grecaptcha: string) => {
      if (!grecaptcha) return;

      const formValues = form.getValues();

      await signUpUser({ ...formValues, grecaptcha });
    },
    [form, signUpUser],
  );

  const onRecaptchaError = useCallback(() => {
    addNoticeBanner({
      text: formatMessage(messages.joinFormError),
      palette: 'caution',
    });
  }, [addNoticeBanner, formatMessage]);

  const submitForm = useMemo(() => {
    return handleSubmit(async (formData) => {
      await (isRecaptchaEnabled ? reCaptchaRef.current?.executeRecaptcha() : signUpUser(formData));
    });
  }, [handleSubmit, isRecaptchaEnabled, reCaptchaRef, signUpUser]);

  // ────────────────────────────────────────────────────────────────────

  const monthNames = getMonthNames().map((key) => formatMessage(joinFormMonthMessageMap[key]));

  // ────────────────────────────────────────────────────────────────────

  const shouldShowLoadingSubmitBtn = isSubmitting || isLoading || isExecutingRecaptcha;
  const shouldDisableSubmitBtn = isLoadingRecaptcha || !isValid;

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper safeAreaEdges={SAFE_AREA_VIEW_EDGES}>
      <Form.Container
        reCaptchaRef={isRecaptchaEnabled ? reCaptchaRef : undefined}
        reCaptchaMachine={isRecaptchaEnabled ? reCaptchaMachine : undefined}
        onRecaptchaVerify={isRecaptchaEnabled ? onRecaptchaVerify : undefined}
        onRecaptchaError={isRecaptchaEnabled ? onRecaptchaError : undefined}
      >
        <Form.Title>{formatMessage(messages.joinTitle)}</Form.Title>

        {onPressSignIn ? (
          <Form.Subtitle direction="row">
            <BodyText size={3}>{formatMessage(messages.joinSubtitle)} </BodyText>

            <TextLink
              accessibilityLabel={formatMessage(messages.loginTitle)}
              onPress={onPressSignIn}
            >
              {formatMessage(messages.loginTitle)}
            </TextLink>
          </Form.Subtitle>
        ) : (
          <View style={styles.titleSpacingPlaceholder} />
        )}

        {/* ========== first name ========== */}

        <Form.Group>
          <Form.TextField
            name="firstName"
            control={control}
            rules={{ required: true }}
            label={formatMessage(messages.formFirstName)}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="name-given"
            autoCapitalize="words"
            clearButtonType="icon"
            notice={errors.firstName?.message}
            disabled={isLoading}
            testID="join-form.first-name"
          />
        </Form.Group>

        {/* ========== last name ========== */}

        <Form.Group>
          <Form.TextField
            name="lastName"
            control={control}
            rules={{ required: true }}
            label={formatMessage(messages.formLastName)}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="name-family"
            autoCapitalize="words"
            clearButtonType="icon"
            notice={errors.lastName?.message}
            disabled={isLoading}
            testID="join-form.last-name"
          />
        </Form.Group>

        {/* ========== email ========== */}

        <Form.Group>
          <Form.TextField
            name="email"
            control={control}
            rules={{ required: true }}
            keyboardType="email-address"
            label={formatMessage(messages.formEmail)}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="email"
            clearButtonType="icon"
            notice={errors.email?.message}
            disabled={Boolean(predefinedEmail) || isLoading}
          />
        </Form.Group>

        {/* ========== phone number ========== */}

        <Form.Group>
          <Form.TextFieldPhoneNumber
            name="phoneNumber"
            control={control}
            rules={{ required: true }}
            label={formatMessage(messages.formPhoneNumber)}
            clearButtonType="icon"
            maxLength={10}
            hideSubmitButton
            onSubmit={submitForm}
            notice={errors.phoneNumber?.message}
            disabled={isLoading}
            helperText={formatMessage(messages.joinPhoneNumberHelperText)}
            testID="join-form.phone-number"
          />
        </Form.Group>

        {/* ========== zip code ========== */}

        {isZipCodeFieldEnabled ? (
          <Form.Group>
            <Form.TextFieldZipCode
              name="zipCode"
              control={control}
              rules={{ required: true }}
              label={formatMessage(messages.formZipCode)}
              clearButtonType="icon"
              maxLength={8}
              hideSubmitButton
              onSubmit={submitForm}
              notice={errors.zipCode?.message}
              disabled={isLoading}
              helperText={formatMessage(messages.joinZipCodeHelperText)}
            />
          </Form.Group>
        ) : null}

        {/* ========== password ========== */}

        {hasPassword ? (
          <Form.Group>
            <Form.TextFieldPassword
              name="password"
              control={control}
              rules={{ required: true }}
              label={formatMessage(messages.formPassword)}
              clearButtonType="icon"
              hideSubmitButton
              onSubmit={submitForm}
              autoComplete="password-new"
              notice={errors.password?.message}
              disabled={isLoading}
            />
          </Form.Group>
        ) : null}

        {/* ========== birthday ========== */}

        <Form.Group>
          <Form.BirthdayPicker
            name="birthday"
            control={control}
            label={formatMessage(messages.formBirthday)}
            accessibilityLabel={formatMessage(messages.formBirthday)}
            monthLabel={formatMessage(messages.joinFormMonth)}
            dayLabel={formatMessage(messages.joinFormDay)}
            helperText={formatMessage(
              isBirthdayRequired
                ? messages.joinBirthdayHelperTextRequired
                : messages.joinBirthdayHelperText,
            )}
            monthNames={monthNames}
          />
        </Form.Group>

        {/* ========== terms and conditions ========== */}

        <TermsAndConditions control={control} errorMessage={errors.terms?.message} />

        {/* ========== ========== ========== */}

        <Form.SubmitButton
          isLoading={shouldShowLoadingSubmitBtn}
          disabled={shouldDisableSubmitBtn}
          accessibilityLabel={formatMessage(messages.joinCreateAccount)}
          onPress={submitForm}
        >
          {formatMessage(messages.joinCreateAccount)}
        </Form.SubmitButton>

        {onPressUseDifferentAccount ? (
          <BodyText sizeMatch={['14']} style={styles.differentAccountContainer}>
            <FormattedMessage
              description="Join Form | Already have an account"
              defaultMessage="Already have an account?"
            />{' '}
            <TextLink
              sizeMatch={['14']}
              onPress={onPressUseDifferentAccount}
              disabled={shouldShowLoadingSubmitBtn}
            >
              <FormattedMessage
                description="Join Form | Sign in with a different email"
                defaultMessage="Sign in with a different email"
              />
            </TextLink>
          </BodyText>
        ) : null}
      </Form.Container>
    </PageWrapper>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  joinFormError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Join Form | Error',
  },
  joinTitle: {
    defaultMessage: 'Welcome! Create your account',
    description: 'Join | Title',
  },
  joinSubtitle: {
    defaultMessage: 'Already have an account?',
    description: 'Join | Subtitle',
  },
  loginTitle: {
    defaultMessage: 'Sign in',
    description: 'Login | Title',
  },
  formFirstName: {
    defaultMessage: 'First name',
    description: 'Form | First Name',
  },
  formLastName: {
    defaultMessage: 'Last name',
    description: 'Form | Last Name',
  },
  formEmail: {
    defaultMessage: 'Email',
    description: 'Form | Email',
  },
  formPhoneNumber: {
    defaultMessage: 'Phone number',
    description: 'Form | Phone Number',
  },
  joinPhoneNumberHelperText: {
    defaultMessage: 'In case there’s an issue with your order',
    description: 'Join | Phone Number Helper Text',
  },
  formZipCode: {
    defaultMessage: 'Zip code',
    description: 'Form | Zip Code',
  },
  joinZipCodeHelperText: {
    defaultMessage: 'To see your local menu, offers and rewards',
    description: 'Join | Zip Code Helper Text',
  },
  formPassword: {
    defaultMessage: 'Password',
    description: 'Form | Password',
  },
  formBirthday: {
    defaultMessage: 'Birthday',
    description: 'Form | Birthday',
  },
  joinFormMonth: {
    defaultMessage: 'Month',
    description: 'Join Form | Birthday | Month',
  },
  joinFormDay: {
    defaultMessage: 'Day',
    description: 'Join Form | Birthday | Day',
  },
  joinBirthdayHelperText: {
    defaultMessage: '(optional) So we can celebrate your big day',
    description: 'Join Form | Birthday Helper Text',
  },
  joinBirthdayHelperTextRequired: {
    defaultMessage: 'Required to create your account',
    description: 'Join Form | Birthday Helper Text | Required',
  },
  joinCreateAccount: {
    defaultMessage: 'Create account',
    description: 'Join | Create Account',
  },
});

const joinFormMonthMessages = defineMessages({
  joinFormMonthJanuary: {
    defaultMessage: 'January',
    description: 'Join Form | Month Option | January',
  },
  joinFormMonthFebruary: {
    defaultMessage: 'February',
    description: 'Join Form | Month Option | February',
  },
  joinFormMonthMarch: {
    defaultMessage: 'March',
    description: 'Join Form | Month Option | March',
  },
  joinFormMonthApril: {
    defaultMessage: 'April',
    description: 'Join Form | Month Option | April',
  },
  joinFormMonthMay: {
    defaultMessage: 'May',
    description: 'Join Form | Month Option | May',
  },
  joinFormMonthJune: {
    defaultMessage: 'June',
    description: 'Join Form | Month Option | June',
  },
  joinFormMonthJuly: {
    defaultMessage: 'July',
    description: 'Join Form | Month Option | July',
  },
  joinFormMonthAugust: {
    defaultMessage: 'August',
    description: 'Join Form | Month Option | August',
  },
  joinFormMonthSeptember: {
    defaultMessage: 'September',
    description: 'Join Form | Month Option | September',
  },
  joinFormMonthOctober: {
    defaultMessage: 'October',
    description: 'Join Form | Month Option | October',
  },
  joinFormMonthNovember: {
    defaultMessage: 'November',
    description: 'Join Form | Month Option | November',
  },
  joinFormMonthDecember: {
    defaultMessage: 'December',
    description: 'Join Form | Month Option | December',
  },
});

const joinFormMonthMessageMap = {
  'general.month.january': joinFormMonthMessages.joinFormMonthJanuary,
  'general.month.february': joinFormMonthMessages.joinFormMonthFebruary,
  'general.month.march': joinFormMonthMessages.joinFormMonthMarch,
  'general.month.april': joinFormMonthMessages.joinFormMonthApril,
  'general.month.may': joinFormMonthMessages.joinFormMonthMay,
  'general.month.june': joinFormMonthMessages.joinFormMonthJune,
  'general.month.july': joinFormMonthMessages.joinFormMonthJuly,
  'general.month.august': joinFormMonthMessages.joinFormMonthAugust,
  'general.month.september': joinFormMonthMessages.joinFormMonthSeptember,
  'general.month.october': joinFormMonthMessages.joinFormMonthOctober,
  'general.month.november': joinFormMonthMessages.joinFormMonthNovember,
  'general.month.december': joinFormMonthMessages.joinFormMonthDecember,
} as const;

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_VIEW_EDGES: Edges = ['bottom'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  titleSpacingPlaceholder: {
    height: theme.spacing['8'],
  },
  differentAccountContainer: {
    marginTop: theme.spacing['10'],
    marginBottom: theme.spacing['6'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type JoinFormProps = Readonly<{
  predefinedEmail?: string;
  isBirthdayRequired?: boolean;
  hasPassword?: boolean;
  shouldUseRecaptcha?: boolean;
  hasZipCode?: boolean; // To be removed together with CELS-3186-enable-zip-code-field.
  isLoading?: boolean;

  onPressJoin: ((payload: JoinFormData) => Promise<void>) | ((payload: JoinFormData) => void);
  onPressSignIn?: () => void;
  onPressUseDifferentAccount?: () => void;
}>;
